import React, { useState } from 'react';

import { themes } from '../../../module/ThemeManager';
import { useThemeContext } from '../../../hook/useThemeManager';
import { Box, Container } from '@mui/material';
import { EsSignupHeader } from '../../Header/EsSignupHeader';

import { EsSignupForm, SignUpdata } from '../../EsSignupForm/EsSignupForm';
import { TUserField } from '../../../redux/user/actions';
import { ROUTES } from '../../../types/global/routes.types';
import { Spotlight } from '../../Spotlight/Spotlight';
import { AnimationBackground } from '../../Spotlight/AnimationBackground';
import { TOnChangePayload } from '../../EsSignupForm/Interests';
import { useSearchParams } from 'react-router-dom';
import { isBrandAuthFlow } from '../../../utils/brandAuthFlow';

export interface ISignUpProps {
    onSubmitSignUpOTP: (data: SignUpdata, code?: string[]) => void;
    checkUserDataAvailability: (data: SignUpdata, callBack: () => void, field: TUserField) => void;
    userNameAvailable: (userName: string) => void;
    getSignUpOTP: (data: SignUpdata, callBack: () => void, resend?: boolean) => void | Promise<unknown>;
    updateUserDetails: (details: TOnChangePayload, callBack?: () => void) => void;
    loading?: boolean;
    otpLoading?: boolean;
    resendInProgress?: boolean;
    initialFormData?: Partial<SignUpdata>;
}

const EsSignupContainer: React.FC<ISignUpProps> = ({
    onSubmitSignUpOTP,
    checkUserDataAvailability,
    userNameAvailable,
    getSignUpOTP,
    loading,
    otpLoading,
    resendInProgress,
    updateUserDetails,
    initialFormData,
}) => {
    const { themeStyle, darkModeEnabled } = useThemeContext();
    const [searchParams] = useSearchParams();
    const [workspaceChoice, setWorkspaceChoice] = useState<'brand' | 'creator'>(() => {
        const redirect = searchParams.get('redirect');
        const view = searchParams.get('dashboard_view') ?? searchParams.get('view') ?? searchParams.get('type');
        return isBrandAuthFlow(redirect, view) ? 'brand' : 'creator';
    });
    const [showBg, setShowBg] = useState(true);
    const handleBeyondFirstStep = (data: SignUpdata, callBack: () => void, field: TUserField, step: number) => {
        checkUserDataAvailability(data, callBack, field);
        setShowBg(false);
    };

    return (
        <Box
            sx={{
                width: '100vw',
                height: '100vh',
                minHeight: '100vh',
                justifyContent: 'center',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                ...(darkModeEnabled && {
                    backgroundImage: `linear-gradient(to bottom, ${themes[themeStyle].true_black} 0%, ${themes[themeStyle].true_white} 500%)`,
                }),
                ...(!darkModeEnabled && {
                    backgroundColor: themes[themeStyle].tag_white_light,
                }),
            }}>
            <EsSignupHeader route={ROUTES.ESLOGIN} />
            <Spotlight withoutBg={!showBg} />

            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'row',
                }}>
                <Container
                    sx={{
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <EsSignupForm
                        getSignUpOTP={getSignUpOTP}
                        userNameAvailable={userNameAvailable}
                        onSubmitSignUpOTP={onSubmitSignUpOTP}
                        checkUserDataAvailability={handleBeyondFirstStep}
                        updateUserDetails={updateUserDetails}
                        loading={loading}
                        otpLoading={otpLoading}
                        resendInProgress={resendInProgress}
                        formStyle={{ maxHeight: '900px' }}
                        initialFormData={initialFormData}
                        workspaceChoice={workspaceChoice}
                        onWorkspaceChoiceChange={setWorkspaceChoice}
                    />
                </Container>
                {showBg && <AnimationBackground workspaceChoice={workspaceChoice} />}
            </Box>
        </Box>
    );
};

export { EsSignupContainer };
